import type { TaskCard, TaskSubtaskWaiting, TaskType } from '@shared/types/tasks';
import { useAuthStore } from '@/stores/authStore';
import { taskTypeOf } from './task-types';
import { isWaitingOn, subtasksWaitingOn } from './task-waiting';

/** T-92: what TaskCardMeta's pieces work out from a card, shared by the Board card and the sub-task list. */

/** Whose turn it is for me: on the task itself, or on its open sub-tasks (mySubtasks: those). */
export interface TaskTurn {
  /** My turn, on the task or one of its open sub-tasks. */
  yourTurn: boolean;
  /** My turn on the task itself. */
  ownTurn: boolean;
  /** Waiting on someone or something, and not me. */
  waitingOnOthers: boolean;
  /** The sub-tasks waiting on me (empty when there are none). */
  mySubtasks: TaskSubtaskWaiting['tasks'];
}

export function useTaskTurn(task: TaskCard): TaskTurn {
  const meId = useAuthStore((s) => s.user?.id);
  const ownTurn = isWaitingOn(task.waiting_on, meId);
  const mySubtasks = subtasksWaitingOn(task, meId);
  const yourTurn = ownTurn || mySubtasks.length > 0;
  return { yourTurn, ownTurn, waitingOnOthers: task.waiting_on.length > 0 && !yourTurn, mySubtasks };
}

/** The fields its type shows: the type (when not the default), size, kind, priority and PRs. */
export function taskCardFields(task: TaskCard, types: TaskType[] | undefined, hideType?: boolean) {
  const type = types ? taskTypeOf(types, task) : undefined;
  return {
    shownType: type && !type.is_default && !hideType ? type : null,
    size: type?.fields.includes('size') && task.size ? task.size : null,
    kind: type?.fields.includes('kind') && task.kind ? task.kind : null,
    // SF-T-151: a Tasks panel passes no types, so it shows a priority that's set.
    priority: (!type || type.fields.includes('priority')) && task.priority ? task.priority : null,
    // A Tasks panel passes no types, so it shows them.
    prs: !type || type.fields.includes('prs') ? task.prs : [],
  };
}

/** Whether TaskCardCounts has anything to show. */
export const hasTaskCardCounts = (task: TaskCard) => Boolean(task.subtasks || task.repeat || task.chat_count || task.attachment_count);

/** Whether TaskCardPeople has anything to show. */
export const hasTaskCardPeople = (task: TaskCard, turn: TaskTurn) => Boolean(task.waiting_on.length || turn.yourTurn || task.assignee_ids.length);
